import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  Tag,
  Package,
  History,
  LogOut,
  ClipboardList,
  PanelLeftClose,
  PanelLeft,
  FileSpreadsheet,
  ClipboardCheck,
  Users,
  UserCog,
  GitBranch,
  Database,
  type LucideIcon,
} from 'lucide-react'
import { useMemo } from 'react'
import { RfidMobileSidebar } from '@/components/layout/RfidMobileSidebar'
import { RfidDrawerProvider, useRfidDrawer } from '@/contexts/RfidDrawerContext'
import { useAuthStore } from '@/stores/authStore'
import { useRegionStore } from '@/stores/regionStore'
import { canAccessCustoutValidation } from '@/config/regions'
import { displayUserName, cn } from '@/utils/cn'
import { ThemeSwitcher } from '@/components/ui/ThemeSwitcher'
import { canAccessRfidEngineAdmin } from '@/utils/rfidAccess'
import { APP_NAME, APP_SUBTITLE } from '@/constants/brand'
import logo from '@/assets/logo.png'

type NavItem = {
  to: string
  label: string
  subtitle: string
  icon: LucideIcon
  indiaOnly?: boolean
}

const BASE_NAV: NavItem[] = [
  {
    to: '/dashboard',
    label: 'Overview',
    subtitle: 'Live tag & material visibility',
    icon: LayoutDashboard,
  },
  {
    to: '/movement-document',
    label: 'Movement Document',
    subtitle: 'Document partners, locations & tags',
    icon: ClipboardList,
  },
  {
    to: '/tag-journey',
    label: 'Tag Journey',
    subtitle: 'Story path for one RFID tag',
    icon: Tag,
  },
  {
    to: '/party-journey',
    label: 'Party Journey',
    subtitle: 'Movements by sender or receiver',
    icon: Users,
  },
  {
    to: '/current-state',
    label: 'Current State',
    subtitle: 'Latest snapshot per tag or line item',
    icon: Package,
  },
  {
    to: '/movements',
    label: 'Movements',
    subtitle: 'Paginated movement history',
    icon: History,
  },
  {
    to: '/reports',
    label: 'RFID Reports',
    subtitle: 'Filter, preview & export Excel',
    icon: FileSpreadsheet,
  },
  {
    to: '/validation-report',
    label: 'RFID Validation Report',
    subtitle: 'Draft vs SAP CUSTOUT tags',
    icon: ClipboardCheck,
    indiaOnly: true,
  },
  {
    to: '/c2-c1-mapping',
    label: 'Asset Life Cycle',
    subtitle: 'Partner & plant path visualiser',
    icon: GitBranch,
  },
  {
    to: '/admin/draft-users',
    label: 'Draft Users',
    subtitle: 'CUSTOUT draft allowlist',
    icon: UserCog,
  },
]

const ENGINE_NAV: NavItem = {
  to: '/admin/rfid-tags',
  label: 'RFID Tags',
  subtitle: 'Master list & mapping report (RFID Admin)',
  icon: Database,
}

export default function AppLayout() {
  return (
    <RfidDrawerProvider collapseStorageKey="rfid-tracking-re-fe.sidebarCollapsed">
      <AppLayoutInner />
    </RfidDrawerProvider>
  )
}

function AppLayoutInner() {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const { sidebarCollapsed: collapsed, toggleSidebarCollapsed } = useRfidDrawer()
  const regionView = useRegionStore((s) => s.view)
  const availableRegions = useRegionStore((s) => s.availableRegions)
  const showValidation = canAccessCustoutValidation(regionView, availableRegions)

  const nav = useMemo(() => {
    const regionalNav = BASE_NAV.filter((item) => !item.indiaOnly || showValidation)
    if (!canAccessRfidEngineAdmin(user)) return regionalNav
    return [...regionalNav, ENGINE_NAV]
  }, [showValidation, user])

  const onLogout = async () => {
    await logout()
    navigate('/login', { replace: true })
  }

  return (
    <div className="relative h-full overflow-hidden bg-background">
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 hidden flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-all duration-200 lg:flex',
          collapsed ? 'w-16' : 'w-60'
        )}
      >
        <div
          className={cn(
            'rfid-sidebar-brand px-3',
            collapsed ? 'justify-center' : 'gap-3 px-4'
          )}
        >
          <img
            src={logo}
            alt="LEAP"
            className={cn('brightness-0 invert', collapsed ? 'h-7' : 'h-8')}
          />
          {!collapsed ? (
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold">{APP_NAME}</div>
              <div className="truncate text-[10px] uppercase tracking-wider text-white/50">
                {APP_SUBTITLE}
              </div>
            </div>
          ) : null}
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto p-2">
          {nav.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/dashboard'}
              title={label}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-lg border-2 px-2.5 py-2 text-sm transition-colors',
                  collapsed && 'justify-center px-2',
                  isActive
                    ? 'border-white bg-black text-white shadow-sm'
                    : 'border-transparent text-white/90 hover:bg-black/20'
                )
              }
            >
              <Icon className="h-4 w-4 shrink-0" />
              {!collapsed ? <span className="truncate">{label}</span> : null}
            </NavLink>
          ))}
        </nav>

        <div className="space-y-2 border-t border-sidebar-border p-2">
          <button
            type="button"
            className="hidden w-full items-center justify-center gap-2 rounded-lg px-2 py-2 text-xs text-white/80 hover:bg-black/20 lg:flex"
            onClick={toggleSidebarCollapsed}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!collapsed}
          >
            {collapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
            {!collapsed ? 'Collapse' : null}
          </button>

          {!collapsed ? (
            <ThemeSwitcher variant="sidebar" />
          ) : (
            <div className="flex justify-center">
              <ThemeSwitcher
                variant="icon"
                className="!h-9 !w-9 border-white/15 bg-black/20 text-white hover:bg-black/30"
              />
            </div>
          )}

          {!collapsed ? (
            <div className="truncate px-2 text-xs text-white/60">{displayUserName(user)}</div>
          ) : null}

          <button
            type="button"
            onClick={onLogout}
            title="Sign out"
            className={cn(
              'flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-white/90 hover:bg-black/20',
              collapsed && 'justify-center px-2'
            )}
          >
            <LogOut className="h-4 w-4 shrink-0" />
            {!collapsed ? 'Sign out' : null}
          </button>
        </div>
      </aside>

      <RfidMobileSidebar />

      <div
        className={cn(
          'flex h-full min-h-0 min-w-0 flex-col transition-[margin] duration-200',
          collapsed ? 'lg:ml-16' : 'lg:ml-60'
        )}
      >
        <main className="rfid-app-canvas flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
